/*
 * promo-bogo.js
 * ---------------------------------------------------------------------------
 * "Buy One Get One" cart promo for one or more SKUs.
 *
 * How it works
 *   For every PAID unit of the configured SKU in the cart, we keep one matching
 *   FREE unit in the cart as a separate line item (same variant) tagged with the
 *   line property `_bogo_free=true`. That free line's quantity always mirrors
 *   the paid quantity exactly:
 *
 *       paid qty 3  ->  free line qty 3   (6 units total)
 *       paid qty 0  ->  free line removed
 *
 *   Each SKU is handled independently: the free unit is ALWAYS the same variant
 *   as the paid unit. SKU1 paid -> SKU1 free; SKU1 + SKU2 paid -> one free SKU1
 *   and one free SKU2 (never two free SKU2).
 *
 *   The "free" part is made free at checkout by a Shopify automatic discount
 *   (Admin -> Discounts -> Buy X Get Y). THIS SCRIPT ONLY MANAGES QUANTITIES.
 *   It does not (and cannot) change prices.
 *
 * Enabling the promo
 *   Admin -> Online Store -> Themes -> Customize -> Theme settings -> "Promo BOGO":
 *     - "Enable Buy One Get One promo" (checkbox)
 *     - "BOGO promo SKU"            (text, default 1470-24X40)
 *   layout/theme.liquid renders those into `window.promo_bogo_one` /
 *   `window.promo_bogo_sku`, and snippets/include_js.liquid only loads this file
 *   when the checkbox is on. The defaults below are a runtime fallback.
 *   Nothing happens while the flag is false.
 * ---------------------------------------------------------------------------
 */
(function () {
    'use strict';

    /* ---------------------------------------------------------------------
     * Promo configuration (safe to hardcode / override before this file runs)
     * ------------------------------------------------------------------- */
    if (typeof window.promo_bogo_one === 'undefined') {
        window.promo_bogo_one = false; // <-- flip to true to launch the promo
    }
    if (typeof window.promo_bogo_sku === 'undefined') {
        window.promo_bogo_sku = '1470-24X40';
    }

    var FREE_PROP = '_bogo_free';   // marks the free line (underscore = hidden from customer)
    var PROMO_PROP = '_bogo_promo'; // promo identifier, helps auditing/orders
    var PROMO_ID = 'bogo_one';

    var syncing = false;
    var syncTimer = null;
    var syncAttempts = 0;
    var MAX_ATTEMPTS = 5;

    /* ---------------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------------- */
    function isEnabled() {
        return window.promo_bogo_one === true || window.promo_bogo_one === 'true';
    }

    function cartRoot() {
        try {
            return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        } catch (e) {
            return '/';
        }
    }

    function fetchJSON(path, options) {
        return fetch(cartRoot() + path, options).then(function (response) {
            if (!response.ok) {
                throw new Error('HTTP ' + response.status + ' on ' + path);
            }
            return response.json();
        });
    }

    function jsonHeaders() {
        return {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        };
    }

    // Reads a line-item property regardless of whether Shopify returns
    // `properties` as an object or as an array of {name,value} / [name,value].
    function getProperty(item, key) {
        var props = item && item.properties;
        if (!props) return undefined;
        if (Array.isArray(props)) {
            for (var i = 0; i < props.length; i++) {
                var entry = props[i];
                if (Array.isArray(entry)) {
                    if (entry[0] === key) return entry[1];
                } else if (entry && entry.name === key) {
                    return entry.value;
                }
            }
            return undefined;
        }
        return props[key];
    }

    function isFreeLine(item) {
        var value = getProperty(item, FREE_PROP);
        return value === true || value === 'true';
    }

    // promo_bogo_sku may hold several SKUs separated by commas or newlines.
    function promoSkus() {
        return String(window.promo_bogo_sku || '')
            .split(/[,\n]+/)
            .map(function (sku) { return sku.trim().toLowerCase(); })
            .filter(Boolean);
    }

    function skuMatches(item) {
        if (!item || item.sku === null || item.sku === undefined) return false;
        return promoSkus().indexOf(String(item.sku).trim().toLowerCase()) !== -1;
    }

    /* ---------------------------------------------------------------------
     * Planning: compare cart state against the "free qty === paid qty" rule
     * and produce the minimal set of cart operations.
     * ------------------------------------------------------------------- */
    function buildPlan(cart) {
        var items = (cart && cart.items) || [];
        var paidQty = {}; // variant_id -> quantity of paid units
        var freeLines = {}; // variant_id -> { qty, keys: [] }

        items.forEach(function (item) {
            if (!skuMatches(item) || !item.variant_id) return;

            if (isFreeLine(item)) {
                var bucket = freeLines[item.variant_id] || (freeLines[item.variant_id] = { qty: 0, keys: [] });
                bucket.qty += item.quantity;
                bucket.keys.push(item.key);
            } else {
                paidQty[item.variant_id] = (paidQty[item.variant_id] || 0) + item.quantity;
            }
        });

        var variants = {};
        Object.keys(paidQty).forEach(function (id) { variants[id] = true; });
        Object.keys(freeLines).forEach(function (id) { variants[id] = true; });

        var changes = []; // { id: lineKey, quantity }
        var adds = [];    // { id: variantId, quantity }

        Object.keys(variants).forEach(function (variantId) {
            var wanted = paidQty[variantId] || 0;
            var existing = freeLines[variantId] ? freeLines[variantId].qty : 0;

            if (wanted === existing) return; // already balanced

            if (wanted === 0) {
                // Paid item gone -> remove every free line for this variant.
                freeLines[variantId].keys.forEach(function (key) {
                    changes.push({ id: key, quantity: 0 });
                });
                return;
            }

            if (freeLines[variantId] && freeLines[variantId].keys.length) {
                // Update the first free line, drop any duplicates.
                var keys = freeLines[variantId].keys;
                changes.push({ id: keys[0], quantity: wanted });
                for (var i = 1; i < keys.length; i++) {
                    changes.push({ id: keys[i], quantity: 0 });
                }
            } else {
                adds.push({ id: Number(variantId), quantity: wanted });
            }
        });

        return { changes: changes, adds: adds };
    }

    function applyPlan(plan) {
        var jobs = [];

        plan.changes.forEach(function (change) {
            jobs.push(fetchJSON('cart/change.js', {
                method: 'POST',
                headers: jsonHeaders(),
                body: JSON.stringify({ id: change.id, quantity: change.quantity })
            }));
        });

        if (plan.adds.length) {
            var properties = {};
            properties[FREE_PROP] = 'true';
            properties[PROMO_PROP] = PROMO_ID;

            var items = plan.adds.map(function (add) {
                return { id: add.id, quantity: add.quantity, properties: properties };
            });

            jobs.push(fetchJSON('cart/add.js', {
                method: 'POST',
                headers: jsonHeaders(),
                body: JSON.stringify({ items: items })
            }));
        }

        return Promise.all(jobs);
    }

    /* ---------------------------------------------------------------------
     * UI refresh - reuse the theme's own refresh so the drawer, header badge
     * and Rebuy flyout all update in sync.
     * ------------------------------------------------------------------- */
    function refreshCartUI() {
        try {
            if (typeof window.customCartRefresh === 'function') {
                window.customCartRefresh();
            }
        } catch (e) { /* non-fatal */ }

        try {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (e) { /* non-fatal */ }
    }

    /* ---------------------------------------------------------------------
     * Core reconciliation loop
     * ------------------------------------------------------------------- */
    function schedule() {
        clearTimeout(syncTimer);
        syncTimer = setTimeout(function () { sync('scheduled'); }, 250);
    }

    function sync() {
        if (!isEnabled() || !promoSkus().length) return;

        if (syncing) {
            return;
        }

        syncing = true;
        syncAttempts++;

        fetchJSON('cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (cart) {
                var plan = buildPlan(cart);
                var hasWork = plan.changes.length > 0 || plan.adds.length > 0;

                if (!hasWork) {
                    syncing = false;
                    syncAttempts = 0;
                    return null;
                }

                return applyPlan(plan).then(function () {
                    syncing = false;
                    refreshCartUI();

                    // Re-verify once the cart has settled (bounded to avoid loops).
                    if (syncAttempts < MAX_ATTEMPTS) {
                        schedule();
                    } else {
                        syncAttempts = 0;
                    }
                });
            })
            .catch(function (error) {
                syncing = false;
                console.warn('[promo-bogo] sync failed:', error);
            });
    }

    /* ---------------------------------------------------------------------
     * Wiring: react to every cart-changing path in this theme.
     * ------------------------------------------------------------------- */
    var CART_EVENTS = [
        'theme:cart:change',   // main.js addToCart()
        'cart:refresh',        // main.js addToCart()
        'BOLD_COMMON_cart_loaded',
        'rebuy:cart.change',
        'rebuy.refresh',
        'rebuy.add',
        'rebuy:smartcart.init',
        'rebuy:smartcart.show'
    ];

    function wrapAddToCart() {
        if (typeof window.addToCart !== 'function' || window.addToCart.__bogoWrapped) return;
        var original = window.addToCart;
        var wrapped = function () {
            var result = original.apply(this, arguments);
            if (result && typeof result.then === 'function') {
                result.then(schedule, schedule);
            } else {
                schedule();
            }
            return result;
        };
        wrapped.__bogoWrapped = true;
        window.addToCart = wrapped;
    }

    function observeCartBadge() {
        if (!window.MutationObserver) return;
        var selectors = ['.badge.badge-cart', '.badge-cart'];
        selectors.forEach(function (selector) {
            var node = document.querySelector(selector);
            if (node) {
                new MutationObserver(schedule).observe(node, {
                    childList: true,
                    characterData: true,
                    subtree: true
                });
            }
        });
    }

    function init() {
        CART_EVENTS.forEach(function (eventName) {
            document.addEventListener(eventName, schedule, false);
            document.documentElement.addEventListener(eventName, schedule, false);
        });

        wrapAddToCart();
        observeCartBadge();

        // Initial pass + a couple of delayed passes for slow cart hydration.
        schedule();
        setTimeout(schedule, 1200);
        setTimeout(schedule, 3000);
    }

    // Expose a manual trigger for debugging: window.promoBogoSync()
    window.promoBogoSync = schedule;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
